<script setup lang="ts">
import { type PropType, provide, ref } from "vue";
import type { UIColor } from "../../..";
import { CompositionBackgroundCardInjectionKey } from "../../helpers.js";

defineProps({
  /*
      Set height to 100%
    */
  explodeHeight: {
    type: Boolean,
    default: false,
  },

  /*
      Omits padding from loader
    */
  loadingCollapseHeight: {
    type: Boolean,
    default: false,
  },

  /*
      Should Component respond to loading state
    */
  loadingEnabled: {
    type: Boolean,
    default: false,
  },

  /*
      The color of the loading animation
    */
  loadingColor: {
    type: String as PropType<UIColor>,
    default: "--color-primary",
  },

  /*
      The origin of the "loading done" push-in scaling animation
    */
  loadingTransformOrigin: {
    type: String,
    default: "top center",
  },

  /*
      Is data being loaded
    */
  loading: {
    type: Boolean,
    default: false,
  },
});

const backgroundsPopulated = ref(false);

provide(CompositionBackgroundCardInjectionKey, backgroundsPopulated);
</script>

<template>
  <div
    :class="[
      $style.container,
      $style[explodeHeight ? 'explodeHeight' : ''],
      backgroundsPopulated ? 'composition-contains-cards' : '',
      $style[loadingEnabled ? 'loadingEnabled' : 'loadingDisabled'],
      $style[loading ? 'loadingTrue' : 'loadingFalse'],
      $style[loadingCollapseHeight ? 'loadingCollapseHeight' : ''],
    ]"
  >
    <div :class="[$style.backgrounds, 'composition-backgrounds']">
      <slot name="backgrounds" />
    </div>

    <div
      :class="[
        $style.loader,
        $style[loadingEnabled ? 'loadingEnabled' : 'loadingDisabled'],
        $style[loading ? 'loadingTrue' : 'loadingFalse'],
        $style[loadingCollapseHeight ? 'loadingCollapseHeight' : ''],
      ]"
      :style="{ 'background-color': `var(${loadingColor})` }"
    />

    <div
      :class="[
        $style.layouts,
        'composition-layouts',
        $style[loadingEnabled ? 'loadingEnabled' : 'loadingDisabled'],
        $style[loading ? 'loadingTrue' : 'loadingFalse'],
      ]"
      :style="{ 'transform-origin': `var(${loadingTransformOrigin})` }"
    >
      <slot name="layouts" />
    </div>
  </div>
</template>

<style module>
.container {
  display: flex;
  width: 100%;
  position: relative;
}

.container.loadingEnabled.loadingTrue {
  min-height: calc(12rem);
}

.container.loadingEnabled.loadingTrue.loadingCollapseHeight {
  min-height: calc(6rem);
}

.explodeHeight {
  height: 100%;
}

.backgrounds {
  display: flex;
  align-items: stretch;
  position: absolute;
  width: 100%;
  height: 100%;
}

.loader {
  position: absolute;
  left: calc(50% - 3rem);
  top: 3rem;
  mask-position: center center;
  mask-repeat: no-repeat;
  mask-size: contain;
  mask-image: url("./assets/loader.svg");
  transform: translate3d(0, 0, 0);
}

.loader.loadingCollapseHeight {
  top: 0;
}

.layouts {
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: visible;
}

.loader.loadingDisabled {
  display: none;
}

.loader.loadingEnabled {
  opacity: 0;
  transition: opacity 0.1s ease-in-out;
}

.loader.loadingTrue {
  width: 6rem;
  height: 6rem;
  opacity: 1;
}

.loader.loadingFalse {
  opacity: 0;
}

.layouts.loadingEnabled {
  opacity: 0;
  transition: opacity 0.1s ease-in-out, transform 0.1s ease-in-out;
  transform-origin: top center;
}

.layouts.loadingFalse {
  opacity: 1;
}

:global(.composition-contains-cards .composition-layouts .clip-path) {
  clip-path: inset(0 0 0 0 round var(--border-radius-medium));
}
</style>
